import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { Badge, Descriptions, Flex } from 'antd';
import {
  ActionButton,
  categoriesToHierarchy,
  ContentSegments,
  DetailLayout,
  DropdownItem,
  EmptyData,
  ErrorAlertWithDrawer,
  GetApiErrorMessage,
  getDatePeriodString,
  isLocked,
  LockedBy,
  ObjectHeader,
  ObjectType,
  Padding,
  PermissionCode,
  PopconfirmDelete,
  Spinner,
  TotalEntries,
  useCheckLockAndNavigate,
  useClassifierDataTypesBy,
  useGetClassifierQuery,
  useHasPermission,
  useMutateClassifierQuery,
  useMutationErrors,
  useShowLockData,
  useAlertContext,
  useQueryParams,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { useParams } from 'react-router-dom';
import { ClassifierBreadcrumbs } from './ClassifierBreadcrumbs';
import { CategoryUploadDownloadActions } from './uploadDownload/CategoryUploadDownloadActions';
import { ClassifierUploadDownloadActions } from './uploadDownload/ClassifierUploadDownloadActions';
import styled from 'styled-components';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ClassifierDetail.translations.json';
import { CategoryDetailTable } from './table/CategoryDetailTable';
import { CategoryHierarchyViewTable } from './table/CategoryHierarchyViewTable';
import { path } from '../../paths';
import { useNavigateTo } from '../../paths/useNavigateTo';

const { addTranslations, useTranslation, t } =
  i18nNsFactory('classifier-details');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export const ClassifierDetail = () => {
  const params = useParams();
  const classifierId = Number(params.id);
  if (isNaN(classifierId)) throw new Error('Classifier ID not provided');

  const { t } = useTranslation();
  const [{ segment = 'categories' }] = useQueryParams();

  const navigateToClassifierEdit = useNavigateTo(path.classifierEdit, {
    id: classifierId,
  });
  const navigateToClassifierClone = useNavigateTo(path.classifierClone, {
    id: classifierId,
  });
  const navigateToIndex = useNavigateTo(path.classifierDirectory);
  const { setSuccess } = useAlertContext();

  const { lockData, refetchLockData } = useShowLockData(
    ObjectType.Classifier,
    classifierId,
  );

  const { mutate } = useMutateClassifierQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { data, isLoading } = useGetClassifierQuery(classifierId, {
    placeholderData: keepPreviousData,
  });

  const categoryCount = useMemo(
    () =>
      (data?.categories?.length || 0) +
      (data?.categoryGroups?.length || 0) +
      (data?.combinedGroups?.length || 0),
    [data],
  );

  const handleCancel = useCallback(() => navigateToIndex(), [navigateToIndex]);

  const checkLockAndNavigateToEdit = useCheckLockAndNavigate(
    refetchLockData,
    lockData,
    () => navigateToClassifierEdit({ segment }),
  );

  const handleDelete = useCallback(() => {
    clearMutationErrors();
    mutate(
      {
        id: classifierId,
      },
      {
        onSuccess: () => {
          navigateToIndex();
          setSuccess(t('alert.success.deleted'));
        },
        onError: ({ response }) => {
          setMutationErrors(response?.data.errors || []);
        },
      },
    );
  }, [
    clearMutationErrors,
    mutate,
    classifierId,
    navigateToIndex,
    setSuccess,
    t,
    setMutationErrors,
  ]);

  const hasPermissionToManage = useHasPermission(
    PermissionCode.CLASSIFIER_MANAGE,
  );

  const dropdownItems = useMemo(
    () =>
      hasPermissionToManage
        ? [
            {
              key: 'clone',
              label: (
                <DropdownItem onClick={() => navigateToClassifierClone()}>
                  {t('button.clone.label')}
                </DropdownItem>
              ),
            },
            {
              disabled: isLocked(lockData),
              key: 'delete',
              label: (
                <PopconfirmDelete
                  stopClickPropagation
                  onDelete={handleDelete}
                  genitiveModelName={t('delete.genitive')}
                />
              ),
            },
          ]
        : [],
    [
      navigateToClassifierClone,
      handleDelete,
      lockData,
      t,
      hasPermissionToManage,
    ],
  );

  const classifierDataTypesById = useClassifierDataTypesBy('id');

  const dataType = useMemo(
    () => classifierDataTypesById.get(data?.dataTypeId || 0),
    [classifierDataTypesById, data?.dataTypeId],
  );

  const descriptionItems = useMemo(() => {
    const items = [
      {
        key: 'validPeriod',
        label: t('field.validPeriod.label'),
        children: getDatePeriodString(
          data?.validPeriod[0],
          data?.validPeriod[1],
        ),
      },
      {
        key: 'hasCategories',
        label: t('field.hasCategories.label'),
        children: data
          ? data.hasCategories
            ? t('field.hasCategories.value.yes')
            : t('field.hasCategories.value.no')
          : '',
      },
    ];
    if (!data?.hasCategories) {
      items.push({
        key: 'dataTypeId',
        label: t('field.dataTypeId.label'),
        children: dataType?.name ?? '',
      });

      if (dataType?.code !== 'NUMBER') {
        items.push({
          key: 'codeFormat',
          label: t('field.codeFormat.label'),
          children: data?.codeFormat ?? '',
        });
      }
    }
    items.push({
      key: 'description',
      label: t('field.description.label'),
      children: data?.description ?? '',
    });

    return items;
  }, [data, dataType, t]);

  const contentSegments = useMemo(() => {
    const hierarchy = data?.categories
      ? categoriesToHierarchy(data.categories)
      : [];

    return [
      {
        value: 'categories',
        label: (
          <Flex gap="small" align="center">
            {t('segment.categories')}
            <Badge count={data?.categories?.length} />
          </Flex>
        ),
        children: (
          <CategoryDetailTable
            name="categories"
            categories={data?.categories}
          />
        ),
      },
      {
        value: 'categoryGroups',
        label: (
          <Flex gap="small" align="center">
            {t('segment.category-groups')}
            <Badge count={data?.categoryGroups.length} />
          </Flex>
        ),
        children: (
          <CategoryDetailTable
            name="categoryGroups"
            categories={data?.categoryGroups}
          />
        ),
      },
      {
        value: 'combinedGroups',
        label: (
          <Flex gap="small" align="center">
            {t('segment.combined-groups')}
            <Badge count={data?.combinedGroups.length} />
          </Flex>
        ),
        children: (
          <CategoryDetailTable
            name="combinedGroups"
            categories={data?.combinedGroups}
          />
        ),
      },
      {
        value: 'hierarchy',
        label: (
          <Flex gap="small" align="center">
            {t('segment.hierarchy')}
            <Badge count={hierarchy.length} />
          </Flex>
        ),
        children: data && <CategoryHierarchyViewTable hirarchy={hierarchy} />,
      },
    ];
  }, [data, t]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <DetailLayout
      infoIconUrl={InfoIconUrl.CLASSIFIER_DETAIL}
      type="detail"
      side={{
        header: (
          <Padding>
            <ObjectHeader
              upperText={data?.code}
              lowerText={data?.name}
              upperPlaceholder={''}
              lowerPlaceholder={''}
            />
          </Padding>
        ),
        content: (
          <Padding>
            <Descriptions items={descriptionItems} column={1} />
          </Padding>
        ),
        footer: (
          <LockedBy
            id={classifierId}
            entity={data}
            objectType={ObjectType.Classifier}
          />
        ),
      }}
      breadCrumb={<ClassifierBreadcrumbs data={{ classifier: data }} />}
      footerContent={<TotalEntries count={categoryCount} />}
      headerConfig={{
        actions: (
          <>
            {data?.hasCategories ? (
              <CategoryUploadDownloadActions
                id={classifierId}
                onSuccess={(response) => console.log(response)}
              />
            ) : (
              <ClassifierUploadDownloadActions
                id={classifierId}
                onSuccess={(response) => console.log(response)}
              />
            )}
            <ActionButton
              type="abort"
              layoutType="detail"
              onClick={handleCancel}
            />
            {hasPermissionToManage && (
              <ActionButton
                disabled={isLocked(lockData)}
                type="mainAction"
                layoutType="detail"
                onClick={() => checkLockAndNavigateToEdit()}
              />
            )}
          </>
        ),
        dropdownMenu: hasPermissionToManage
          ? {
              items: dropdownItems,
            }
          : undefined,
      }}
      alert={<ErrorAlertWithDrawer validationMessagesMap={mutationErrors} />}
    >
      <ContentWrapper>
        {data?.hasCategories ? (
          <ContentSegments segments={contentSegments} />
        ) : (
          <EmptyWrapper>
            <EmptyData>{t('title.no-categories')}</EmptyData>
          </EmptyWrapper>
        )}
      </ContentWrapper>
    </DetailLayout>
  );
};

const ContentWrapper = styled(Padding)`
  width: 100%;
  min-width: fit-content;
  min-height: fit-content;
`;

const EmptyWrapper = styled.div`
  padding-top: 150px;
`;
